import React, { useState, useEffect, Component } from "react"
import { graphql } from "gatsby"
import { Logo, Search, Close, Expand, Collapse, Arrow } from "../../icons"
import useArticleQuery from "../../../hooks/use-article-query"
import ArticleCard from "../../cards/article"
import parse from "html-react-parser"
import StructuredText from "../custom-structured-text-renderer"
import { Asset, Link } from "../../../../plugins/gatsby-plugin-atollon"
import Slider from "react-slick"

class BlockUpdate extends Component {
  shouldComponentUpdate(nextProps) {
    // Compare relevant props to determine if re-render is necessary
    return nextProps.someRelevantProp !== this.props.someRelevantProp;
  }

  render() {
    return (<>{ this.props.children }</>)
  }
}

const ArticleArchive = ({ featuredArticle, featuredCta }) => {
  const { allDatoCmsArticle, allDatoCmsCategory } = useArticleQuery()
  const [currentSlide, setCurrentSlide] = React.useState(1)
  const sliderRef = React.useRef(null)

  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState(false)
  const [selectedSort, setSelectedSort] = useState('newest')
  const [selectedTopics, setSelectedTopics] = useState([])
  const [filteredArticles, setFilteredArticles] = useState([])
  const [expandedFilters, setExpandedFilters] = useState([]) // Track multiple expanded filters

  const toggleSelection = (selectedArray, setSelectedArray, item) => {
    if (selectedArray.includes(item)) {
      setSelectedArray(selectedArray.filter(selected => selected !== item))
    } else {
      setSelectedArray([...selectedArray, item])
    }
  }

  const toggleFilterExpand = (filterName) => {
    if (expandedFilters.includes(filterName)) {
      setExpandedFilters(expandedFilters.filter(name => name !== filterName)) // Collapse
    } else {
      setExpandedFilters([...expandedFilters, filterName]) // Expand
    }
  }

  // Helper function: Check if any filter is active
  const isFilterActive = () => {
    return searchTerm || selectedTopics.length > 0
  }

  // Apply filters based on query string
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const category = params.get("category")

    if (category) {
      setSelectedTopics([category]) // Add initial category filter
    } else {
      clearFilters()
    }

  }, [])

  // Consolidated filtering logic
  useEffect(() => {
    let articles = [...(allDatoCmsArticle?.nodes || [])];

    if (featuredArticle && !isFilterActive()) {
      articles = articles.filter(article => article.title !== featuredArticle.title);
    }

    if (searchTerm) {
      articles = articles.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedTopics.length > 0) {
      articles = articles.filter(article =>
        article.categories.some(category => selectedTopics.includes(category.title))
      );
    }

    if (selectedSort === "title") {
      articles.sort((a, b) => a.title.localeCompare(b.title));
    } else {
      articles.sort((a, b) => new Date(b.meta.firstPublishedAt) - new Date(a.meta.firstPublishedAt));
    }

    setFilteredArticles(articles);
  }, [featuredArticle, searchTerm, selectedTopics, selectedSort, allDatoCmsArticle?.nodes]);

  useEffect(() => {
    if (sliderRef?.current) {
      sliderRef.current.slickGoTo(0); // Reset slider to first slide
    }
  }, [filteredArticles]);

  const getHeadingText = () => {
    let heading = ''
    
    const activeFilters = []
    if (selectedTopics.length > 0) activeFilters.push(selectedTopics.join(" / "))

    if (activeFilters.length > 0) {
      heading = `${filteredArticles.length} Results for<br />${activeFilters.join(" / ")}${searchTerm ? ` / ${searchTerm}"` : ""}`
    }

    if (activeFilters.length === 0 && searchTerm) {
      heading = `${filteredArticles.length} Results for<br />${searchTerm ? ` "${searchTerm}"` : ""}`
    }

    return parse(heading)
  }

  const clearFilters = () => {
    setSearchTerm("")
    setSelectedSort('newest')
    setSelectedTopics([])
  }

  const settings = {
    dots: false,
    infinite: false,
    fade: true,
    speed: 500,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: currentSlide ? currentSlide - 1 : 0,
    beforeChange: (current, next) => {
      setCurrentSlide(next + 1)
    },
  }

  return (
    <>
      { featuredArticle && !isFilterActive() &&
        <section className="page-banner page-banner--feature">
          <div className="page-banner__inner">
            {featuredArticle.thumbnail && <Asset className="page-banner__bg" asset={featuredArticle.thumbnail} />}
            <div className='page-banner__content'>
              <div className='page-banner__left'>
                {featuredArticle.categories?.length > 0 && <span>{parse(featuredArticle.categories[0].title)}</span>}
                {featuredArticle.title && <h2><Link to={featuredArticle.permalink}>{parse(featuredArticle.title)}</Link></h2>}
              </div>
              <div className='page-banner__right'>
                { featuredArticle.excerpt && <StructuredText data={featuredArticle.excerpt} /> }
                { featuredArticle.permalink && <Link to={featuredArticle.permalink}><Arrow/></Link> }
              </div>
            </div>
          </div>
        </section>
      } 
      {isFilterActive() && (
          <section className="title-content title-content--news">
            <div className="title-content__inner">
              <div className="title-content__wrapper">
                <div className="line line--vertical center-top"></div>
                <div className="title-content__title">
                  <h1>{getHeadingText()}</h1>
                </div>
              </div>
            </div>
          </section>
      )}
      <section className={`article-archive rest`}>
        <div className="article-archive__inner">
          <div className="article-archive__filter">
            {isFilterActive() ? (
              <button className="btn" onClick={clearFilters}>Clear Search <Close /></button>
            ) : (
              <button className="btn btn--orange" onClick={() => setFilter(!filter)}>Filter</button>
            )}
            <div className="line line--horizon center-left"></div>
            <div className="line line--horizon center-right"></div>
          </div>
          <div className="article-archive__wrapper">
            <div className="line line--vertical center-bottom"></div>
            <div className="line line--vertical center-top"></div>
            {filteredArticles?.length > 0 &&
              <div className="article-archive__items">
                {
                  filteredArticles?.map((el, i) => {
                    if (i === 2 && !isFilterActive() && featuredCta?.length > 0) {
                      return (
                        <React.Fragment key={i}>
                          <div className='article-archive__item'>
                            <BlockUpdate someRelevantProp={el.slug}>
                              <ArticleCard {...el} />
                            </BlockUpdate>
                          </div>
                          { featuredCta?.length > 0 &&
                            <div className='article-archive__item article-archive__item--feature'>
                              <div className="cta-slider">
                                <div className="cta-slider__inner">
                                  {featuredCta?.length > 0 &&
                                  <div className="cta-slider__items">
                                    <Slider ref={sliderRef} {...settings}>
                                      {featuredCta.map((item, index) => (
                                      <div className="cta-slider__item" key={index}>
                                        {item.backgroundImage && <div className="cta-slider__asset"><Asset asset={item.backgroundImage} /></div>}

                                        <div className="cta-slider__item-inner">
                                          {item.subtitle && <h3>{parse(item.subtitle)}</h3>}
                                          <div className="cta-slider__content">
                                            {item.title && <h2>{parse(item.title)}</h2>}
                                            {item.richContent && <StructuredText data={item.richContent} />}

                                            <div className="cta-slider__link">
                                              <Link
                                                to={item?.link[0].link[0].externalLink ? item?.link[0].link[0].externalLink : item?.link[0].link[0].internalLink.permalink}
                                              >
                                                <span className="btn btn--orange">{featuredCta?.length > 9 ? '' : '0'}{currentSlide} / {featuredCta?.length > 9 ? '' : '0'}{featuredCta?.length}</span>
                                                <span>{item?.link[0].title}<Arrow /></span>
                                              </Link>
                                            </div>
                                          </div>
                                        </div>
                                      </div>))}
                                    </Slider>
                                  </div>}
                                </div>
                                <div className='cta-slider__navigate'>
                                  <div onClick={() => sliderRef.current.slickPrev()}></div>
                                  <div onClick={() => sliderRef.current.slickNext()}></div>
                                </div>
                              </div>
                            </div>
                          }
                        </React.Fragment>
                      )
                    } else {
                      return (
                        <div className='article-archive__item' key={i}>
                          <BlockUpdate someRelevantProp={el.slug}>
                            <ArticleCard {...el} />
                          </BlockUpdate>
                        </div>
                      )
                    }
                  })
                }
              </div>
            }
          </div>
        </div>
      </section>

      <section className={`filter${filter ? " filter--active" : ""}`}>
        <div className="filter__inner">
          <div className="filter__header">
            <Logo />
            <button className="btn btn--border-white" onClick={() => setFilter(!filter)}>Close</button>
          </div>
          <form onSubmit={(e) => {e.preventDefault();setSearchTerm(searchTerm);setFilter(!filter)}} className="filter__search">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <button><Search /></button>
          </form>
          <div className="filter__items">
            <div className={`filter__item ${expandedFilters.includes("sort") ? "expanded" : ""}`}>
              <h4>
                <button onClick={() => toggleFilterExpand("sort")}>
                  Sort <span>{expandedFilters.includes("sort") ? <Collapse /> : <Expand />}</span>
                </button>
              </h4>
              <ul>
                <li>
                  <button
                    onClick={() => setSelectedSort('title')}
                    className={selectedSort === 'title' ? "active" : ""}
                  >
                    A - Z
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setSelectedSort('newest')}
                    className={selectedSort === 'newest' ? "active" : ""}
                  >
                    Newest to Oldest
                  </button>
                </li>
              </ul>
            </div>
            <div className={`filter__item ${expandedFilters.includes("topic") ? "expanded" : ""}`}>
              <h4>
                <button onClick={() => toggleFilterExpand("topic")}>
                  by Topic <span>{expandedFilters.includes("topic") ? <Collapse /> : <Expand />}</span>
                </button>
              </h4>
              <ul>
                {allDatoCmsCategory?.nodes.map((el, i) => (
                  <li key={i}>
                    <button
                      onClick={() => toggleSelection(selectedTopics, setSelectedTopics, el.title)}
                      className={selectedTopics.includes(el.title) ? "active" : ""}
                    >
                      {el.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <button className="filter__excess" onClick={() => setFilter(!filter)} />
      </section>
    </>
  )
}

export default ArticleArchive

export const query = graphql`
  fragment ArticleArchiveProps on DatoCmsArticleArchive {
    ... on DatoCmsArticleArchive {
      id
      featuredArticle {
        title
        slug
        jsonLd
        permalink
        seoMetaTags {
          tags
        }
        meta {
          firstPublishedAt(formatString: "DD MMMM YYYY")
        }
        excerpt {
          value
        }
        thumbnail {
          ...InternalAssetProps
        }
        categories {
          title
        }
      }
      featuredCta {
        title
        subtitle
        richContent {
          value
        }
        backgroundImage {
          ...InternalAssetProps
        }
        link {
          ...LinkWithTitleProps
        }
      }
    }
  }
`